import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, UserMinus, RefreshCw, ArrowRightLeft, Calendar, Plus, Eye, Trash2, Clock } from "lucide-react";

const REQUEST_TYPES = [
  { value: "new", label: "New Hire", icon: UserPlus, color: "bg-green-500" },
  { value: "term", label: "Termination", icon: UserMinus, color: "bg-red-500" },
  { value: "change", label: "Change", icon: RefreshCw, color: "bg-blue-500" },
  { value: "transfer", label: "Transfer", icon: ArrowRightLeft, color: "bg-purple-500" },
  { value: "leave", label: "Leave", icon: Calendar, color: "bg-yellow-500" }
];

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-800",
  completed: "bg-blue-100 text-blue-800"
};

export default function HRRequests({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dealerships, setDealerships] = useState([]);
  const [filter, setFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    employee_first_name: "",
    employee_last_name: "",
    employee_suffix: "",
    request_type: "",
    start_date: "",
    employee_id: "",
    employee_id_redacted: false,
    dealership: user.dealership || "",
    job_title: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const effectiveRole = previewRole || user.role;
  const canManageRequests = user.role === "admin" || user.role === "super_admin";

  useEffect(() => {
    fetchRequests();
    fetchDealerships();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/hr-requests`);
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch HR requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDealerships = async () => {
    try {
      const response = await axios.get(`${API}/public/dealerships`);
      setDealerships(response.data);
    } catch (error) {
      console.error("Failed to fetch dealerships:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_first_name.trim()) {
      toast.error("Please enter employee first name");
      return;
    }
    if (!formData.employee_last_name.trim()) {
      toast.error("Please enter employee last name");
      return;
    }
    if (!formData.request_type) {
      toast.error("Please select a request type");
      return;
    }
    if (!formData.start_date) {
      toast.error("Please select a start date");
      return;
    }
    if (!formData.dealership) {
      toast.error("Please select a dealership");
      return;
    }
    if (!formData.job_title.trim()) {
      toast.error("Please enter job title");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/hr-requests`, formData);
      toast.success("HR request submitted successfully");
      setShowCreateForm(false);
      setFormData({
        employee_first_name: "",
        employee_last_name: "",
        employee_suffix: "",
        request_type: "",
        start_date: "",
        employee_id: "",
        employee_id_redacted: false,
        dealership: user.dealership || "",
        job_title: "",
        notes: ""
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit HR request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/hr-requests/${requestId}`, { status: newStatus });
      toast.success(`Request ${newStatus}`);
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error("Failed to update request status");
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this HR request?")) return;
    
    try {
      await axios.delete(`${API}/hr-requests/${requestId}`);
      toast.success("HR request deleted");
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredRequests = filter === "all" 
    ? requests 
    : requests.filter(r => r.status === filter);

  const getRequestTypeInfo = (type) => {
    return REQUEST_TYPES.find(t => t.value === type) || REQUEST_TYPES[0];
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">HR Requests</h1>
            <p className="text-slate-500 dark:text-slate-400">Manage employee onboarding, terminations, and changes</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-[#7a1d42] hover:bg-[#5a1632]"
            data-testid="new-hr-request-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New HR Request
          </Button>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({requests.filter(r => r.status === "pending").length})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({requests.filter(r => r.status === "approved").length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({requests.filter(r => r.status === "completed").length})</TabsTrigger>
            <TabsTrigger value="denied">Denied ({requests.filter(r => r.status === "denied").length})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a1d42] mx-auto"></div>
            <p className="mt-4 text-slate-500">Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No HR requests found</h3>
              <p className="text-slate-400 mt-1">Create a new request to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredRequests.map((request) => {
              const typeInfo = getRequestTypeInfo(request.request_type);
              const TypeIcon = typeInfo.icon;
              return (
                <Card 
                  key={request.id} 
                  className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedRequest(request)}
                  data-testid={`hr-request-${request.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full ${typeInfo.color} flex items-center justify-center`}>
                          <TypeIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 dark:text-white">
                            {request.employee_first_name} {request.employee_last_name}
                            {request.employee_suffix && ` ${request.employee_suffix}`}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {typeInfo.label} • {request.job_title} • {request.dealership}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Start: {new Date(request.start_date).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-slate-400">
                            Submitted by {request.created_by_name}
                          </p>
                        </div>
                        <Badge className={STATUS_COLORS[request.status]}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Request Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#7a1d42]" />
              New HR Request
            </DialogTitle>
            <DialogDescription>
              Submit a new HR request for employee changes
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Request Type Selection */}
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <div className="grid grid-cols-5 gap-2">
                {REQUEST_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => updateField("request_type", type.value)}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                        formData.request_type === type.value
                          ? "border-[#7a1d42] bg-[#7a1d42]/10"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`request-type-${type.value}`}
                    >
                      <div className={`w-8 h-8 rounded-full ${type.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Employee Name */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="employee_first_name">First Name *</Label>
                <Input
                  id="employee_first_name"
                  value={formData.employee_first_name}
                  onChange={(e) => updateField("employee_first_name", e.target.value)}
                  placeholder="John"
                  data-testid="employee-first-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_last_name">Last Name *</Label>
                <Input
                  id="employee_last_name"
                  value={formData.employee_last_name}
                  onChange={(e) => updateField("employee_last_name", e.target.value)}
                  placeholder="Doe"
                  data-testid="employee-last-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_suffix">Suffix</Label>
                <Select value={formData.employee_suffix} onValueChange={(value) => updateField("employee_suffix", value)}>
                  <SelectTrigger data-testid="employee-suffix">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">None</SelectItem>
                    <SelectItem value="Jr.">Jr.</SelectItem>
                    <SelectItem value="Sr.">Sr.</SelectItem>
                    <SelectItem value="II">II</SelectItem>
                    <SelectItem value="III">III</SelectItem>
                    <SelectItem value="IV">IV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => updateField("start_date", e.target.value)}
                data-testid="start-date"
                required
              />
            </div>

            {/* Employee ID */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="employee_id">Employee ID</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="redact" className="text-sm text-slate-500">Redact</Label>
                  <Switch
                    id="redact"
                    checked={formData.employee_id_redacted}
                    onCheckedChange={(checked) => updateField("employee_id_redacted", checked)}
                    data-testid="employee-id-redact"
                  />
                </div>
              </div>
              <Input
                id="employee_id"
                value={formData.employee_id}
                onChange={(e) => updateField("employee_id", e.target.value)}
                placeholder="EMP-12345"
                disabled={formData.employee_id_redacted}
                className={formData.employee_id_redacted ? "bg-slate-100" : ""}
                data-testid="employee-id"
              />
              {formData.employee_id_redacted && (
                <p className="text-xs text-amber-600">Employee ID will be hidden from the request</p>
              )}
            </div>

            {/* Dealership */}
            <div className="space-y-2">
              <Label htmlFor="dealership">Dealership *</Label>
              <Select value={formData.dealership} onValueChange={(value) => updateField("dealership", value)} required>
                <SelectTrigger data-testid="hr-dealership">
                  <SelectValue placeholder="Select dealership" />
                </SelectTrigger>
                <SelectContent>
                  {dealerships.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Title */}
            <div className="space-y-2">
              <Label htmlFor="job_title">Job Title *</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => updateField("job_title", e.target.value)}
                placeholder="Sales Associate"
                data-testid="job-title"
                required
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Any additional information..."
                rows={3}
                data-testid="hr-notes"
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-[#7a1d42] hover:bg-[#5a1632]"
                data-testid="submit-hr-request"
              >
                {submitting ? "Submitting..." : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="sm:max-w-[500px]">
          {selectedRequest && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const typeInfo = getRequestTypeInfo(selectedRequest.request_type);
                    const TypeIcon = typeInfo.icon;
                    return (
                      <div className={`w-10 h-10 rounded-full ${typeInfo.color} flex items-center justify-center`}>
                        <TypeIcon className="w-5 h-5 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle>
                      {selectedRequest.employee_first_name} {selectedRequest.employee_last_name}
                      {selectedRequest.employee_suffix && ` ${selectedRequest.employee_suffix}`}
                    </DialogTitle>
                    <DialogDescription>
                      {getRequestTypeInfo(selectedRequest.request_type).label} Request
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Job Title</p>
                    <p className="font-medium">{selectedRequest.job_title}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Dealership</p>
                    <p className="font-medium">{selectedRequest.dealership}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Start Date</p>
                    <p className="font-medium">{new Date(selectedRequest.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Employee ID</p>
                    <p className="font-medium">
                      {selectedRequest.employee_id_redacted ? "Redacted" : (selectedRequest.employee_id || "Not provided")}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge className={STATUS_COLORS[selectedRequest.status]}>
                      {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Submitted By</p>
                    <p className="font-medium">{selectedRequest.created_by_name}</p>
                  </div>
                </div>
                
                {selectedRequest.notes && (
                  <div>
                    <p className="text-slate-500 text-sm">Notes</p>
                    <p className="text-sm mt-1 p-2 bg-slate-50 rounded">{selectedRequest.notes}</p>
                  </div>
                )}
                
                <div className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Submitted {new Date(selectedRequest.created_at).toLocaleString()}
                </div>
              </div>

              {canManageRequests && (
                <DialogFooter className="flex justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(selectedRequest.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                  <div className="flex gap-2">
                    {selectedRequest.status === "pending" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusUpdate(selectedRequest.id, "denied")}
                          className="text-red-600"
                        >
                          Deny
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(selectedRequest.id, "approved")}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Approve
                        </Button>
                      </>
                    )}
                    {selectedRequest.status === "approved" && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(selectedRequest.id, "completed")}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Mark Completed
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
